import { useState, useEffect, useMemo } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WritersHero from './components/WritersHero';
import WriterCard from './components/WriterCard';
import { supabase } from '../../lib/supabase';
import SEOHead from '../../components/feature/SEOHead';

interface Writer {
  id: string;
  display_name: string;
  username: string;
  bio: string | null;
  avatar_url: string | null;
  join_date: string | null;
  story_count: number;
  total_likes: number;
  total_reads: number;
}

type SortOption = 'most_stories' | 'most_reads' | 'most_likes' | 'newest';

const WRITERS_PER_PAGE = 12;

const WritersPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [writers, setWriters] = useState<Writer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('most_stories');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchWriters = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.rpc('get_writers_with_stats');
        if (error) throw error;

        const enriched: Writer[] = (data || []).map((w: any) => ({
          id:           w.id,
          display_name: w.display_name,
          username:     w.username,
          bio:          w.bio,
          avatar_url:   w.avatar_url,
          join_date:    w.join_date,
          story_count:  Number(w.story_count),
          total_likes:  Number(w.total_likes),
          total_reads:  Number(w.total_reads),
        }));

        setWriters(enriched);
      } catch (err) {
        console.error('Error fetching writers:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchWriters();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, sortBy]);

  const filtered = useMemo(() => {
    let result = [...writers];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (w) =>
          (w.display_name || '').toLowerCase().includes(q) ||
          (w.username || '').toLowerCase().includes(q) ||
          (w.bio || '').toLowerCase().includes(q)
      );
    }

    if (sortBy === 'most_stories') {
      result.sort((a, b) => b.story_count - a.story_count);
    } else if (sortBy === 'most_reads') {
      result.sort((a, b) => b.total_reads - a.total_reads);
    } else if (sortBy === 'most_likes') {
      result.sort((a, b) => b.total_likes - a.total_likes);
    } else if (sortBy === 'newest') {
      result.sort(
        (a, b) =>
          new Date(b.join_date || 0).getTime() - new Date(a.join_date || 0).getTime()
      );
    }

    return result;
  }, [writers, searchQuery, sortBy]);

  const paginated = filtered.slice(0, page * WRITERS_PER_PAGE);
  const hasMore = paginated.length < filtered.length;

  const sortOptions: { value: SortOption; label: string; icon: string }[] = [
    { value: 'most_stories', label: 'Most Stories', icon: 'ri-book-2-line' },
    { value: 'most_reads', label: 'Most Read', icon: 'ri-eye-line' },
    { value: 'most_likes', label: 'Most Liked', icon: 'ri-heart-line' },
    { value: 'newest', label: 'Newest', icon: 'ri-calendar-line' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEOHead
        title="Horror Fiction Authors | Meet the Writers – Sinister Files"
        description="Discover the talented horror fiction authors behind Sinister Files. Browse independent writers of creepypasta, psychological horror, supernatural tales, and dark fiction."
        canonical="https://sinisterfiles.com/writers"
      />
      <Navbar scrolled={scrolled} />

      <WritersHero searchQuery={searchQuery} onSearchChange={setSearchQuery} />

      {/* Filters Bar */}
      <div className="sticky top-16 z-30 bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-red-900/20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-gray-400 text-sm font-['Crimson_Text']">
            {loading ? (
              <span className="animate-pulse">Loading authors...</span>
            ) : (
              <>
                <span className="text-white font-bold">{filtered.length}</span>{' '}
                {filtered.length === 1 ? 'author' : 'authors'} found
              </>
            )}
          </p>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-gray-500 text-xs font-['Crimson_Text'] whitespace-nowrap">Sort by:</span>
            {sortOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSortBy(opt.value)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-['Crimson_Text'] transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  sortBy === opt.value
                    ? 'bg-red-700 text-white'
                    : 'bg-[#1a1a1a] text-gray-400 hover:text-white border border-red-900/20 hover:border-red-900/40'
                }`}
              >
                <i className={opt.icon} />
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Writers Grid */}
      <main className="max-w-7xl mx-auto px-4 py-12">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-[#0f0f0f] border border-red-900/20 rounded-lg overflow-hidden animate-pulse">
                <div className="h-28 bg-zinc-800" />
                <div className="px-6 -mt-10 mb-3">
                  <div className="w-20 h-20 rounded-full bg-zinc-700 border-4 border-[#0f0f0f]" />
                </div>
                <div className="px-6 pb-5 space-y-3">
                  <div className="h-5 bg-zinc-800 rounded w-2/3" />
                  <div className="h-3 bg-zinc-800 rounded w-1/3" />
                  <div className="h-4 bg-zinc-800 rounded" />
                  <div className="h-4 bg-zinc-800 rounded w-4/5" />
                  <div className="h-10 bg-zinc-800 rounded mt-4" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-red-900/10 rounded-full mb-6">
              <i className="ri-user-search-line text-4xl text-red-800" />
            </div>
            <h3 className="text-2xl font-bold text-white font-['Cinzel'] mb-3">No Writers Found</h3>
            <p className="text-gray-500 font-['Crimson_Text'] max-w-md">
              No authors match your search. Try a different name or clear the search.
            </p>
            <button
              onClick={() => setSearchQuery('')}
              className="mt-6 px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-['Crimson_Text'] transition-colors cursor-pointer whitespace-nowrap"
            >
              Clear Search
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {paginated.map((writer, index) => (
                <WriterCard key={writer.id} writer={writer} index={index} />
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-12">
                <button
                  onClick={() => setPage((p) => p + 1)}
                  className="px-10 py-3 bg-[#0f0f0f] border border-red-900/30 hover:border-red-600/60 text-gray-300 hover:text-white rounded-lg text-sm font-['Crimson_Text'] transition-all duration-300 cursor-pointer whitespace-nowrap group"
                >
                  <i className="ri-arrow-down-line mr-2 group-hover:animate-bounce inline-block" />
                  Load More Writers
                  <span className="ml-2 text-gray-600 text-xs">
                    ({filtered.length - paginated.length} remaining)
                  </span>
                </button>
              </div>
            )}

            {!hasMore && filtered.length > WRITERS_PER_PAGE && (
              <p className="text-center text-gray-600 text-sm font-['Crimson_Text'] mt-12">
                You have met all the authors in the archive.
              </p>
            )}
          </>
        )}
      </main>

      <Footer />

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default WritersPage;
